if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        //this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.cart = document.querySelector('mini-cart');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        // "Select your size": when the submit button is marked
        // data-requires-selection, the product's non-colour option(s) (e.g.
        // Size) start unselected. The button reads "Select your size" until the
        // shopper chooses; clicking it before then scrolls to the picker instead
        // of adding to cart. We keep the label in sync on every option change.
        this.requiresSelection = this.submitButton.hasAttribute('data-requires-selection');
        if (this.requiresSelection) {
          // On INITIAL load only, clear the pre-selected non-colour option(s)
          // (e.g. Size) so the shopper must choose one. We do this in JS rather
          // than Liquid so that after a selection the server re-render (which
          // keeps the chosen value :checked) isn't wiped — the active swatch
          // keeps its selected border. Only runs when the page wasn't opened
          // with an explicit ?variant= (a deep-linked variant stays selected).
          const hasExplicitVariant = new URLSearchParams(window.location.search).has('variant');
          if (!hasExplicitVariant) this.clearRequiredSelections();

          this.form.addEventListener('change', this.refreshSelectionState.bind(this));
          this.refreshSelectionState();
        }
      }

      /*
        Resolve the variant picker on every access — never cache it.

        Changing an option re-renders the picker through
        HTMLUpdateUtility.viewTransition, which builds a BRAND NEW
        <variant-selects>, inserts it before the old one, and drops the old one
        500ms later. <product-form> itself is not re-rendered, so its constructor
        never runs again. A reference captured there would therefore keep
        pointing at the discarded node: after switching colour, the size the
        shopper clicked was checked in the live picker while we kept reading the
        detached one, which had nothing checked. The button stayed on "Select
        your size" and add-to-cart was blocked.

        document order works in our favour here: the replacement is inserted
        BEFORE the outgoing node, so both getElementById and querySelector return
        the live picker even during the 500ms overlap.
      */
      get variantSelects() {
        const sectionId = this.dataset.sectionId;
        return (
          (sectionId && document.getElementById(`variant-selects-${sectionId}`)) ||
          this.closest('product-info')?.querySelector('variant-selects') ||
          document.querySelector('variant-selects')
        );
      }

      // Uncheck the pre-selected value of every NON-colour option (initial load).
      clearRequiredSelections() {
        if (!this.variantSelects) return;
        const colorName = (window.theme && theme.optionStrings && theme.optionStrings.color) || 'color';
        this.variantSelects.querySelectorAll('fieldset').forEach((fs) => {
          const named = fs.querySelector('[data-option-name]');
          const legend = fs.querySelector('legend');
          const name = (
            (named && named.getAttribute('data-option-name')) ||
            (legend && legend.textContent) ||
            ''
          ).trim().toLowerCase();
          if (name.includes(colorName)) return; // keep colour selected
          fs.querySelectorAll('input[type="radio"]:checked, input[type="checkbox"]:checked').forEach((i) => {
            i.checked = false;
          });
        });
      }

      // True only when every NON-colour option has a value selected.
      allRequiredOptionsSelected() {
        if (!this.variantSelects) return true;
        const colorName = (window.theme && theme.optionStrings && theme.optionStrings.color) || 'color';
        const fieldsets = this.variantSelects.querySelectorAll('fieldset');
        // Fall back to true if the markup isn't fieldset-based.
        if (!fieldsets.length) return true;
        for (const fs of fieldsets) {
          // Option name: prefer the .swatches[data-option-name], else the legend.
          const named = fs.querySelector('[data-option-name]');
          const legend = fs.querySelector('legend');
          const name = (
            (named && named.getAttribute('data-option-name')) ||
            (legend && legend.textContent) ||
            ''
          ).trim().toLowerCase();
          if (name.includes(colorName)) continue; // colour may be pre-selected
          // A radio/checkbox group counts as selected only if one input is checked.
          const inputs = fs.querySelectorAll('input[type="radio"], input[type="checkbox"]');
          const selects = fs.querySelectorAll('select');
          if (inputs.length) {
            if (![...inputs].some((i) => i.checked)) return false;
          } else if (selects.length) {
            // For a <select> we can't tell "unchosen" from the first option, so
            // a non-empty value counts as selected.
            if (![...selects].some((s) => s.value)) return false;
          }
        }
        return true;
      }

      // Keep the button label/state matched to whether selection is complete.
      refreshSelectionState() {
        if (!this.requiresSelection) return;
        const complete = this.allRequiredOptionsSelected();
        // Clear the "Please select a size" message once a selection is made.
        if (complete) this.toggleSelectSizeError(false);
        this.submitButton.toggleAttribute('data-needs-selection', !complete);
        if (this.submitButton.classList.contains('product-form__submit--soldout')) return; // don't override sold-out
        const selectLabel = (window.theme && theme.variantStrings && theme.variantStrings.selectSize) || 'Select your size';
        this.submitButtonText.textContent = complete ? theme.variantStrings.addToCart : selectLabel;
      }

      scrollToVariantPicker() {
        const target = this.variantSelects || document.querySelector('variant-selects');
        if (!target) return;
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      // The "Please select a size" message(s) shown under each non-colour
      // option's title.
      get selectSizeErrors() {
        const target = this.variantSelects || document.querySelector('variant-selects');
        return target ? target.querySelectorAll('[data-select-size-error]') : [];
      }

      toggleSelectSizeError(show) {
        this.selectSizeErrors.forEach((el) => el.toggleAttribute('hidden', !show));
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        // Require a non-colour option (size) before adding — show a message above
        // the swatches and scroll to the picker.
        if (this.requiresSelection && !this.allRequiredOptionsSelected()) {
          this.toggleSelectSizeError(true);
          this.scrollToVariantPicker();
          return;
        }
        if (this.submitting || this.submitButton.getAttribute('aria-disabled') === 'true') return;
        if (!this.blockSubmitIfRequiredFieldsEmpty(evt)) return;

        this.handleErrorMessage();

        /*
          Guard re-submission with an internal flag rather than aria-disabled.

          The shopper's focus is on this button when they add from the keyboard,
          so flipping aria-disabled on it makes a screen reader announce
          "unavailable" — and that state change wins the race against the
          "Item added to your cart" confirmation, which is the message that
          actually matters. The loading class still gives the visual state.
        */
        this.submitting = true;
        this.submitButton.classList.add('loading');
        //this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${theme.routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = theme.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            // Show "Added to Bag" text temporarily
            this.submitButtonText.textContent = theme.variantStrings.addedToCart;
            setTimeout(() => {
              this.submitButtonText.textContent = theme.variantStrings.addToCart;
            }, 2000);
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitting = false;
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            //this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      blockSubmitIfRequiredFieldsEmpty(event) {
        if (!(this.form instanceof HTMLFormElement)) {
          throw new Error("Expected a form element.");
        }

        const formId = this.form.getAttribute('id');
        const requiredFields = document.querySelectorAll('[required]');
        let allFilled = true;
        for (const field of requiredFields) {
          const belongsToForm = (field.getAttribute('form') === formId || field.closest('form')?.id === formId);
          if (!belongsToForm) continue;
          
          let isValid = true;
          switch (field.type) {
            case 'checkbox':
            case 'radio':
              const group = document.querySelectorAll(`input[name="${field.name}"][type="${field.type}"]`);
              const isChecked = Array.from(group).some(input => input.checked);
              isValid = isChecked;
              break;
            case 'select-one':
              isValid = field.value !== '';
              break;
            case 'textarea':
            case 'text':
            case 'email':
            case 'number':
            case 'url':
            default:
              isValid = field.value.trim() !== '';
              break;
          }
          if (!isValid) {
            allFilled = false;
            field.classList.add('invalid');
          } else {
            field.classList.remove('invalid');
          }
        }
    
        if (!allFilled) {
          event.preventDefault();
          this.handleErrorMessage(theme.accessibilityStrings.fillInAllLineItemPropertyRequiredFields);
          return false;
        }
        return true;
      }
      
      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        this.submitButton.classList.toggle('product-form__submit--soldout', !!disable);
        this.submitButton.classList.toggle('button--full-width', !disable);
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          // When the product still needs a non-colour selection, keep the
          // "Select your size" label instead of "Add to Bag".
          if (this.requiresSelection && !this.allRequiredOptionsSelected()) {
            const selectLabel = (window.theme && theme.variantStrings && theme.variantStrings.selectSize) || 'Select your size';
            this.submitButtonText.textContent = selectLabel;
          } else {
            this.submitButtonText.textContent = theme.variantStrings.addToCart;
          }
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
